import { useEffect, useState } from 'react'
import { Navigate } from 'react-router'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Riquadro } from '../componenti/base.tsx'
import { IstruzioniElement } from '../componenti/IstruzioniElement.tsx'
import { sistemaTelefono } from '../servizi/app-nativa.ts'

/**
 * Messenger interno (479, decisione del committente del 28/10/2026).
 *
 * La messaggistica e' un server Matrix dedicato, con il client Element, in cui si entra con le
 * credenziali del gestionale (servizio di accesso CAS, apps/api/src/rotte/messenger.ts). Dal
 * 29/10/2026 (decisione del committente) si usa dentro il gestionale: la pagina incorpora Element in
 * un riquadro. Il messenger sta su un nome dello stesso dominio del gestionale, quindi il cookie di
 * sessione arriva al CAS anche dal riquadro e l'accesso e' immediato (single sign-on).
 *
 * `?stanza=<alias>` apre il messenger su quella stanza, per esempio la chat riservata di una scheda
 * (`#riservata-…:cidis`). L'indirizzo arriva da GET /auth/io (`indirizzi.messenger`), null finche'
 * MESSENGER_URL non e' configurato.
 *
 * «Apri in una finestra separata» (richiesta del committente del 30/10/2026) porta Element in una
 * finestra a parte e svuota il riquadro: Element tiene una sola sessione per browser e, aperto due
 * volte, chiede quale delle due chiudere. Il riquadro torna con «Riportalo qui»: il gestionale ha
 * Cross-Origin-Opener-Policy same-origin, quindi non tiene il riferimento a una finestra di un'altra
 * origine e non sa quando viene chiusa. Nell'app del telefono non ci sono finestre separate: il
 * comando non compare.
 *
 * Dal telefono (browser, app CIDIS o schermata Home) la pagina invita in cima a usare Element Classic,
 * con i passi per iPhone e Android (decisione di Davide dell'1/11/2026, IstruzioniElement.tsx); gli
 * stessi passi stanno nella guida in fondo, per tutti.
 */
const NOME_FINESTRA = 'messenger-cidis'
const NELL_APP = /CIDIS-App/.test(navigator.userAgent)
const SUL_TELEFONO = sistemaTelefono() !== 'altro'

export function Messenger() {
  const { io } = useSessione()
  const indirizzo = io?.indirizzi?.messenger ?? null
  const stanza = new URLSearchParams(window.location.search).get('stanza')
  // Solo alias o identificativi di stanza di questo server: il parametro non porta altrove.
  const destinazione = stanza && /^[#!][A-Za-z0-9._=\-]+:cidis$/.test(stanza)
    ? `/#/room/${encodeURIComponent(stanza)}` : ''
  const [staccato, setStaccato] = useState(false)

  function apriSeparato() {
    const aperta = window.open(`${indirizzo}${destinazione}`, NOME_FINESTRA,
      'popup,width=1100,height=820')
    // Con le finestre a comparsa bloccate resta il riquadro: meglio che nessun messenger.
    if (aperta) setStaccato(true)
  }

  return (
    <>
      <Intestazione
        titolo="Messenger"
        sottotitolo="Messaggi fra colleghi di CIDIS, cifrati end-to-end."
        azioni={indirizzo && !NELL_APP && !staccato
          ? <Bottone onClick={apriSeparato}>Apri in una finestra separata</Bottone>
          : undefined}
      />

      {indirizzo && SUL_TELEFONO && (
        <div className="messenger-telefono">
          <p><strong>Sul telefono usa l’app Element Classic</strong>, non Element X. Scegli il tuo telefono:</p>
          <IstruzioniElement indirizzo={indirizzo} />
        </div>
      )}

      {!indirizzo && <Avviso>Il messenger non è ancora attivo.</Avviso>}

      {indirizzo && staccato && (
        <Avviso>
          <p>
            Il messenger è aperto in una finestra separata. Se la chiudi, puoi riportarlo in questa pagina.
          </p>
          <div className="messenger-staccato">
            <Bottone onClick={() => setStaccato(false)}>Riportalo qui</Bottone>
          </div>
        </Avviso>
      )}

      {indirizzo && !staccato && (
        <>
          <p className="piccolo tenue messenger-regola">
            <strong>Niente dati dei beneficiari</strong> nei canali e nei messaggi: per parlare di una persona
            usa «Riferimento per il messenger» nella scheda. Fanno eccezione le chat riservate delle schede, che
            si aprono dall’area riservata.
          </p>
          <iframe className="messenger-riquadro" title="Messenger" src={`${indirizzo}${destinazione}`}
                  allow="clipboard-read; clipboard-write; fullscreen" referrerPolicy="no-referrer" />
        </>
      )}

      <details className="messenger-guida">
        <summary>Come funziona: chiave di recupero e app sul telefono</summary>
        <div className="griglia">
          <Riquadro titolo="Che cos’è">
            <p>
              Ogni sede ha il suo spazio. Nello spazio ci sono i gruppi di lavoro e i canali di avviso,
              dove scrivono solo la Direzione e i responsabili.
            </p>
            <p>I messaggi sono cifrati end-to-end: li leggono solo i partecipanti alla conversazione.</p>
            <p>Si entra con l’accesso del gestionale, senza un’altra password.</p>
          </Riquadro>

          <Riquadro titolo="La chiave di recupero">
            <p>
              Al primo accesso Element chiede di impostare il «Backup sicuro». Impostalo e conserva la
              chiave di recupero in un posto sicuro.
            </p>
            <p>
              Senza, su un dispositivo nuovo i messaggi vecchi non si leggono. La chiave la conosci solo
              tu: in CIDIS nessuno la può recuperare.
            </p>
          </Riquadro>

          <Riquadro titolo="Sul telefono">
            <p>Usa l’app Element Classic, non Element X.</p>
            <IstruzioniElement indirizzo={indirizzo} />
          </Riquadro>
        </div>
      </details>
    </>
  )
}

/**
 * Ritorno al servizio di accesso del messenger dopo l'accesso al gestionale.
 *
 * GET /api/cas/login rimanda qui chi non ha ancora una sessione completa. Senza sessione la pagina
 * non arriva nemmeno a disegnarsi: main.tsx mostra l'accesso (password, codice, secondo fattore,
 * password provvisoria da cambiare) e, appena la sessione e' completa, disegna questa rotta, che
 * torna al CAS con lo stesso `service`. Il controllo dell'indirizzo lo rifa' il server: qui si va
 * solo verso /api/cas/login, mai altrove.
 */
export function RitornoAlMessenger() {
  const service = new URLSearchParams(window.location.search).get('service')
  useEffect(() => {
    if (service) window.location.replace(`/api/cas/login?service=${encodeURIComponent(service)}`)
  }, [service])
  if (!service) return <Navigate to="/messaggi" replace />
  return <div className="accesso"><Attesa testo="Ritorno al messenger…" /></div>
}
